import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { SecondaryButton } from '@shared/ui/atoms/buttons/SecondaryButton';
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { Section } from '@shared/ui/organisms/layout/Layout';
import TextField from '@shared/ui/atoms/TextField';
import { Permission } from '@shared/models/user.models';
import { updatePermissions } from '@features/settings/services/ManagementService';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { failureNotification, successNotification } from '@shared/store/notification.duck';
import SelectableContentCard from './components/SelectableContentCard';
import { DialogIdentifiers, openDialogWithPayload } from '@shared/store/dialog.duck';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import {
  canChangeUsersDefaultOffice,
  canChangeUsersRole,
  isCompanyAdminUser,
  isDepartmentAdmin,
  isOfficeAdminUser,
  isTeamAdminUser
} from '@shared/utils/AccessControl';
import Colours from '@shared/ui/atoms/Colours';
import SelectLocationSection from "./components/SelectLocationSection";
import {
  fetchEditUser,
  resetEditUser,
  selectEdittingUser,
  selectHasEditUserErrored,
  selectIsEditUserLoading,
  updateUser
} from '@features/settings/store/editUser.duck';
import UsersLineReports from './components/UsersLineReports';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { AdditionalResponsibilitiesSection } from "./components/AdditionalResponsibilitiesSection";
import { UserHolidayAllowanceSection } from "./components/UserHolidayAllowanceSection";
import { selectAllTeams, selectConfig } from "@shared/store/config.duck";
import { app } from "@microsoft/teams-js";
import { teamsTabs, uiUrl } from "@shared/services/EnvironmentVariables";
import { deepLinkTo, isInTeams } from "@shared/utils/TeamsUtils";
import { useTranslation } from "react-i18next";
import { ChangeUsersTeamDropDownComponent } from "./edit-user-page/ChangeUsersTeamDropDownComponent";
import AccessRequired from "./components/AccessRequired";
import { UserAccessPermittedControl } from "./edit-user-page/UserAccessPermittedControl";
import { ChangeUsersDepartmentDropDownComponent } from "./edit-user-page/ChangeUsersDepartmentDropDownComponent";
import { UserWorkGroupsSection } from "./edit-user-page/UserWorkGroupsSection";
import { selectAllDepartments } from "@features/whereabouts/pages/departments/data/departments.duck";
import { MountedContentCard } from '@shared/ui/atoms/MountedContentCard';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { BirthdaySection } from "./components/BirthdaySection";
import { UserMacAddressSection } from "./components/UserMacAddressSection";
import { UpdateUserField, updateUserField } from '@features/settings/services/UserService';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export default function EditUserPage() {
  const params: any = useParams();
  const dispatch = useDispatch();
  const editingUser = useSelector(selectEdittingUser);
  const isLoading = useSelector(selectIsEditUserLoading);
  const teams = useSelector(selectAllTeams);
  const currentUser = useSelector(selectCurrentUser);
  const config = useSelector(selectConfig);
  const hasEditUserErrored = useSelector(selectHasEditUserErrored);
  const departments = useSelector(selectAllDepartments);
  const {t} = useTranslation();
  const editingUserId = params.userId;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [permission, setPermission] = useState(Permission.Basic);
  const [holidayAllowance, setHolidayAllowance] = useState(0);
  const [subEntityId, setSubEntityId] = useState<string | null>(null);

  const canLoadUserToEdit = useMemo(() => {
    return isCompanyAdminUser(currentUser) || isDepartmentAdmin(currentUser) || isOfficeAdminUser(currentUser) || isTeamAdminUser(currentUser);
  }, [currentUser]);

  useEffect(() => {
    if (isInTeams()) {
      app.getContext().then((context: app.Context) => {
        setSubEntityId(context.page.subPageId ?? null);
      })
    }

    return () => {
      dispatch(resetEditUser());
    }
  }, [dispatch]);

  useEffect(() => {
    if (!editingUser && editingUserId && canLoadUserToEdit) {
      dispatch(fetchEditUser(editingUserId));
    }
  }, [editingUser, editingUserId, canLoadUserToEdit, dispatch]);

  useEffect(() => {
    if (editingUser) {
      setFirstName(editingUser.firstName);
      setLastName(editingUser.lastName);
      setJobTitle(editingUser?.jobTitle ?? '')
      setPermission(editingUser.role);
    }
  }, [editingUser]);

  useEffect(() => {
    setHolidayAllowance(editingUser?.holidayAllowance ?? config?.companyHolidayAllowance ?? 0);
  }, [config, editingUser]);

  const saveChanges = async () => {
    if (!editingUser) return;

    if (firstName === editingUser.firstName && lastName === editingUser.lastName &&
        permission === editingUser.role && holidayAllowance === editingUser.holidayAllowance && jobTitle === editingUser.jobTitle) {
      dispatch(failureNotification('Nothing to update'))
      return;
    }

    try {
      if (jobTitle) {
        await updateUserField(editingUser.userId, UpdateUserField.jobTitle, jobTitle);
      }
      dispatch(updateUser({
        ...editingUser,
        firstName: firstName,
        lastName: lastName,
        holidayAllowance: holidayAllowance,
      }));
      dispatch(successNotification('User updated'))
    } catch (e: any) {
      dispatch(failureNotification('Error. User NOT updated'))
    }
  }

  const onLocationChange = async () => {
    dispatch(fetchEditUser(params.userId));
  }

  const onHolidayAllowanceUpdated = (val: number) => {
    setHolidayAllowance(val);
  }

  const onBackButtonPressed = () => {
    if (subEntityId) {
      // Deep link back to settings page to reset subEntityId
      deepLinkTo(teamsTabs.settings, `${uiUrl}/manage`);
    } else {
      // Else just go back
      window.history.back();
    }
  }

  if (!canLoadUserToEdit) {
    return <Center>
      <AccessRequired visible={true} />
    </Center>
  }

  if (hasEditUserErrored) {
    return <Center>
      <MountedContentCard>
        <BodyRegular weight={600}>Failed to load</BodyRegular>
        <BodyRegular style={{marginBottom: 32}}>Please try again or is the problem persists contact <a href="mailto:support@team-today.com">support@team-today.com</a>.</BodyRegular>
        <PrimaryButton text={"Try again"} fullWidth={true} click={() => window.location.reload()} />
      </MountedContentCard>
    </Center>
  }

  if (!editingUser || !currentUser) {
    return <Center>
      <LoadingSpinner hideBorder={true} />
    </Center>;
  }

  return (
    <React.Fragment>
      {isLoading && <LoadingSpinner fullScreen={true} />}
      <div className="management">
        <GoBackButton onClick={onBackButtonPressed}/>
        <HeadlineLarge className="editTeam__header management__title">{t('settings-page.edit-user.title')}</HeadlineLarge>
        <TextField dataTest="editUserPage-firstName-input" disabled={!canLoadUserToEdit} label="textinput.firstname"
                   onChange={(val) => setFirstName(val)} value={firstName}/>
        <TextField dataTest="editUserPage-lastName-input" disabled={!canLoadUserToEdit} label="textinput.lastname"
                   onChange={(val) => setLastName(val)} value={lastName}/>

        <Section hideBorder={true}>
          <BodyLarge colour={Colours.blue}>{t('settings-page.edit-user.email-address')}</BodyLarge>
          <BodyRegular className="management__row">{editingUser?.email}</BodyRegular>
          <SecondaryButton dataTest="editUserPage-delete-button"
                           disabled={!isCompanyAdminUser(currentUser)}
                           text="button.delete-user"
                           click={() => dispatch(openDialogWithPayload({
                             activeDialog: DialogIdentifiers.DeleteUserDialog,
                             payload: {userInfo: editingUser},
                           }))}/>
        </Section>

        <UserAccessPermittedControl userInfo={editingUser} />

        <TextField disabled={!canLoadUserToEdit}
                   label="textinput.jobTitle"
                   fullWidth={true}
                   onChange={(val) => setJobTitle(val)}
                   value={jobTitle} />

        <ChangeUsersTeamDropDownComponent editingUser={editingUser} teams={teams}  />

        <UserWorkGroupsSection editingUser={editingUser} />

        {departments.length > 0 && <ChangeUsersDepartmentDropDownComponent editingUser={editingUser} departments={departments} />}

        {currentUser?.companyEntity.lineManagementEnabled && <UsersLineReports user={editingUser} />}

        <SelectLocationSection disabled={!canChangeUsersDefaultOffice(currentUser)} user={editingUser}  onLocationChange={onLocationChange} />


        <UserPermissionSelection disabled={!canChangeUsersRole(currentUser)}
                                 permission={permission}
                                 userId={editingUser?.userId}
                                 role={editingUser?.role}
                                 onPermissionChanged={setPermission}/>

        {currentUser?.companyEntity.enableHolidayBooking &&
          <UserHolidayAllowanceSection user={editingUser}
                                       holidayAllowance={holidayAllowance}
                                       onHolidayAllowanceChange={onHolidayAllowanceUpdated} />}

        <AdditionalResponsibilitiesSection user={editingUser} />

        {currentUser?.companyEntity.automaticOfficeCheckinEnabled && <UserMacAddressSection user={editingUser} />}

        <BirthdaySection userId={editingUser.userId}
                         birthdayMonth={editingUser.birthdayMonth}
                         birthdayDay={editingUser.birthdayDay} />

        <PrimaryButton dataTest="editUserPage-save-button" disabled={!canLoadUserToEdit} className="management__loneControl"
                       text="button.save-changes" size={ButtonSize.Large} click={saveChanges}/>
      </div>
    </React.Fragment>
  );
}

function UserPermissionSelection(props: { permission: Permission, userId: string, role: any, onPermissionChanged: any, disabled?: boolean }) {
  const current = useSelector(selectCurrentUser);
  const {t} = useTranslation();
  const isCompanyAdmin = current?.role === Permission.CompanyAdmin;
  const isDepartmentAdmin = current?.role === Permission.DepartmentAdmin;
  const isOfficeAdmin = current?.role === Permission.OfficeAdmin;
  const isTeamAdmin = current?.role === Permission.TeamAdmin;
  const [loading, setLoading] = useState(false);

  const setPermission = async (permission: Permission) => {
    if (!props.disabled) {
      try {
        setLoading(true);
        await updatePermissions(props.userId, permission);
        props.onPermissionChanged(permission)
      } finally {
        setLoading(false);
      }
    }
  }

  if (!props.role) {
    return null;
  }

  return (
    <Section>
      <BodyLarge colour={Colours.blue}>{t('settings-page.edit-user.permissions')}</BodyLarge>
      <div className="editUse__permissions">
        <div>
          <SelectableContentCard disabled={props.disabled || loading}
                                 selected={props.permission === Permission.Basic}
                                 title="settings-page.edit-user.standard-user"
                                 onClick={() => setPermission(Permission.Basic)}>
            <ul>
              <li><BodySmall>{t('settings-page.edit-user.basic-permissions')}</BodySmall></li>
            </ul>
          </SelectableContentCard>

          {(isCompanyAdmin || isTeamAdmin || isOfficeAdmin || isDepartmentAdmin) ?
            <SelectableContentCard disabled={props.disabled || loading}
                                   selected={props.permission === Permission.TeamAdmin}
                                   title="settings-page.edit-user.team-admin"
                                   onClick={() => setPermission(Permission.TeamAdmin)}>
              <ul>
                <li><BodySmall>{t('settings-page.edit-user.team-admin-permissions-1')}</BodySmall></li>
                <li><BodySmall>{t('settings-page.edit-user.team-admin-permissions-2')}</BodySmall></li>
                <li><BodySmall>{t('settings-page.edit-user.team-admin-permissions-3')}</BodySmall></li>
              </ul>
            </SelectableContentCard>
            : null}

          {isCompanyAdmin ?
            <SelectableContentCard disabled={props.disabled || loading} selected={props.permission === Permission.OfficeAdmin}
                                   title="settings-page.edit-user.office-admin" onClick={() => setPermission(Permission.OfficeAdmin)}>
              <ul>
                <li><BodySmall>{t('settings-page.edit-user.office-admin-permissions-1')}</BodySmall></li>
                <li><BodySmall>{t('settings-page.edit-user.office-admin-permissions-2')}</BodySmall></li>
                <li><BodySmall>{t('settings-page.edit-user.office-admin-permissions-3')}</BodySmall></li>
                <li><BodySmall>{t('settings-page.edit-user.office-admin-permissions-4')}</BodySmall></li>
              </ul>
            </SelectableContentCard>
            : null}

          {isCompanyAdmin ?
            <SelectableContentCard disabled={props.disabled || loading}
                                   selected={props.permission === Permission.DepartmentAdmin}
                                   title="settings-page.edit-user.department-admin"
                                   onClick={() => setPermission(Permission.DepartmentAdmin)}>
              <ul>
                <li><BodySmall>{t('settings-page.edit-user.department-admin-permissions-1')}</BodySmall></li>
                  <li><BodySmall>{t('settings-page.edit-user.department-admin-permissions-2')}</BodySmall></li>
              </ul>
            </SelectableContentCard>
            : null}

          {isCompanyAdmin ?
            <SelectableContentCard disabled={props.disabled || loading} selected={props.permission === Permission.CompanyAdmin}
                                   title="settings-page.edit-user.company-admin" onClick={() => setPermission(Permission.CompanyAdmin)}>
              <ul>
                <li><BodySmall>{t('settings-page.edit-user.company-admin-permissions-1')}</BodySmall></li>
                <li><BodySmall>{t('settings-page.edit-user.company-admin-permissions-2')}</BodySmall></li>
                <li><BodySmall>{t('settings-page.edit-user.company-admin-permissions-3')}</BodySmall></li>
              </ul>
            </SelectableContentCard>
            : null}

        </div>
      </div>
    </Section>
  )
}
